<template>
  <div>
    <h2>{{ $t('upload.videos') }}</h2>

    <v-card class="mb-10 pa-6" elevation="0" tile>
      <v-row>
        <v-col cols="12" class="pb-0">
          <v-text-field
            v-model="url"
            :label="$t('realty.label.videoUrl')"
            :error-messages="urlError"
            :disabled="saving"
            outlined
            dense
            hide-details="auto"
            @keyup.enter="addVideo"
          />
        </v-col>
      </v-row>

      <!-- Nome em linha própria: dividir a linha com o link deixava o campo tão estreito que até o
           label era truncado, e nome de vídeo costuma ser uma frase, não uma palavra. -->
      <v-row>
        <v-col cols="12" sm="9">
          <v-text-field
            v-model="title"
            :label="$t('realty.label.videoTitle')"
            :disabled="saving"
            :counter="titleMaxLength"
            :maxlength="titleMaxLength"
            outlined
            dense
            hide-details="auto"
            @keyup.enter="addVideo"
          />
        </v-col>

        <v-col cols="12" sm="3" class="d-flex align-start">
          <v-btn
            color="primary"
            :disabled="!url || saving"
            :loading="saving"
            block
            depressed
            @click="addVideo"
          >
            <v-icon left>mdi-plus</v-icon>
            {{ $t('realty.label.addVideo') }}
          </v-btn>
        </v-col>
      </v-row>

      <v-divider v-if="hasVideos" class="my-5" />

      <v-row v-if="hasVideos">
        <v-col v-for="(video, index) in videos" :key="video.id || index" cols="12" sm="6" md="4">
          <v-card outlined>
            <a :href="video.url" target="_blank" rel="noopener noreferrer">
              <v-img v-if="thumbnailOf(video)" :src="thumbnailOf(video)" height="120px" />
              <div v-else class="grey lighten-2 d-flex align-center justify-center" style="height: 120px">
                <v-icon large color="grey darken-1">mdi-video</v-icon>
              </div>
            </a>

            <v-card-text class="py-2">
              <!-- Nome longo quebra em até duas linhas em vez de ser cortado no meio, e o texto
                   inteiro fica no title para quem passar o mouse -->
              <div class="video-title" :title="video.title || video.url">
                {{ video.title || $t('realty.label.videoWithoutTitle') }}
              </div>
              <div class="caption grey--text text-truncate" :title="video.url">{{ video.url }}</div>
            </v-card-text>

            <v-card-actions class="pt-0">
              <v-spacer />
              <v-btn icon color="error" :disabled="saving" @click="removeVideo(video, index)">
                <v-icon>mdi-delete</v-icon>
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>

      <v-row v-else>
        <v-col>
          <h5 class="text-h5 text-center">{{ $t('realty.label.doesNotHaveVideos') }}</h5>
        </v-col>
      </v-row>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { isSupportedVideoUrl, videoThumbnail } from '@/util/videoUrl'

/**
 * Vídeos do imóvel: só link do YouTube ou do Vimeo, porque é o que a Vista aceita - arquivo de vídeo
 * ela recusa por qualquer caminho.
 *
 * Um componente para os dois modos, ao contrário das fotos, que têm um para criar e outro para
 * editar e duplicam o mesmo código em quatro arquivos:
 *
 * - com `realtyId`: cada link vai para o backend na hora, e a lista vem do store
 * - sem `realtyId` (criação): os links ficam em memória e sobem depois, pelo v-model, quando o
 *   imóvel existir - `addVideos` precisa do id dele
 */
export default {
  name: 'RealtyVideos',

  props: {
    /** Null durante a criação do imóvel: aí os links são acumulados e enviados depois. */
    realtyId: {
      required: false,
      default: null
    },

    /**
     * A imobiliária dona do imóvel. Usuário de imobiliária carrega a própria e o backend ignora este
     * valor; usuário de prestador de serviço não tem e trabalha em nome de um cliente.
     */
    realEstateId: {
      required: false,
      default: null
    },

    /** Usado apenas no modo criação: os links pendentes. */
    value: {
      required: false,
      default: () => []
    }
  },

  data() {
    return {
      // Mesmo limite da coluna title em realty_video
      titleMaxLength: 255,
      url: '',
      title: '',
      urlError: null,
      saving: false,
      pendingVideos: []
    }
  },

  computed: {
    ...mapGetters(['realtyVideos', 'addRealtyVideosSuccess']),

    videos() {
      return this.realtyId ? this.realtyVideos : this.pendingVideos
    },

    hasVideos() {
      return this.videos.length > 0
    }
  },

  watch: {
    realtyId: {
      immediate: true,
      handler(realtyId) {
        if (realtyId) this.fetchRealtyVideos(realtyId)
      }
    },

    pendingVideos(videos) {
      this.$emit('input', videos)
    }
  },

  methods: {
    ...mapActions(['addRealtyVideos', 'deleteRealtyVideos', 'fetchRealtyVideos']),

    async addVideo() {
      this.urlError = null

      if (!isSupportedVideoUrl(this.url)) {
        this.urlError = this.$t('realty.label.unsupportedVideoUrl')
        return
      }

      const video = { url: this.url.trim(), title: this.title.trim() || null }

      if (!this.realtyId) {
        this.pendingVideos = [...this.pendingVideos, video]
        this.resetInput()
        return
      }

      this.saving = true
      await this.addRealtyVideos({
        realtyId: this.realtyId,
        real_estate_id: this.realEstateId,
        realtyVideos: [video]
      })

      if (this.addRealtyVideosSuccess) {
        await this.fetchRealtyVideos(this.realtyId)
        this.resetInput()
      }

      this.saving = false
    },

    async removeVideo(video, index) {
      if (!this.realtyId) {
        this.pendingVideos = this.pendingVideos.filter((it, at) => at !== index)
        return
      }

      this.saving = true
      await this.deleteRealtyVideos({
        realtyId: this.realtyId,
        real_estate_id: this.realEstateId,
        realtyVideosId: [video.id]
      })
      await this.fetchRealtyVideos(this.realtyId)
      this.saving = false
    },

    /** Chamado pelo pai depois de criar o imóvel, para a seleção não ficar pendurada. */
    resetVideos() {
      this.pendingVideos = []
      this.resetInput()
    },

    resetInput() {
      this.url = ''
      this.title = ''
      this.urlError = null
    },

    thumbnailOf(video) {
      return videoThumbnail(video)
    }
  }
}
</script>

<style scoped>
.video-title {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: 2.5em;
  line-height: 1.25em;
  word-break: break-word;
}
</style>
